import { trace } from '@opentelemetry/api';
import { EnvironmentService } from '@/services/isomorphic/EnvironmentService';
import { OTEL_SERVICENAME } from '@/configs/env/public';
import type { AnyAsyncFunction } from '@/type-utils';

/**
 * This is the 'operation.name' attribute that gets appended to the tracing span. It can be
 * found in the Vercel 'Observability' dashboard section when clicking into a custom trace.
 * We will use the OTEL_SERVICENAME from the public environment variables.
 */
const tracer = trace.getTracer(OTEL_SERVICENAME);

/**
 * A decorator to trace method calls on the server side only.
 * On the client side, this decorator does nothing.
 *
 * By adding this decorator to a method, the method will be traced using
 * Opentelemetry to provide insights into the method's performance.
 * @param traceName - The name of the trace. It should follow the format
 * `'ServiceName.MethodName'`. If not provided, the method name will be used.
 * @returns A method decorator that adds tracing functionality.
 *
 * @example
 * class ContentService {
 *  ;@traceable('ContentService.getContent')
 *  public async getContent(): Promise<IContent> {
 *    // method implementation...
 *  }
 * }
 */
export function traceable(traceName?: string) {
  return <T extends object, Fn extends AnyAsyncFunction>(
    target: T,
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<Fn>
  ) => {
    // This cannot be run on the client side, so we return the descriptor as is.
    if ((typeof window !== "undefined")) {
      if (!(process.env.NEXT_PUBLIC_APP_ENV === "prod")) {
        console.warn(
          `The @traceable decorator is not supported on the client side. ` +
            `The method ${propertyKey} will not be traced.`
        );
      }

      return descriptor;
    }

    const targetName = target.constructor?.name ?? 'UnknownClass';
    const methodName = propertyKey;
    const originalMethod = descriptor.value;

    if (!originalMethod) {
      throw new Error(`Descriptor value is undefined for ${methodName}`);
    }

    descriptor.value = async function _(this: T, ...args) {
      // If traceName is provided, use it as the span name, if not
      // provided it will use the method name as a fallback.
      const spanName = 'Custom Trace: ' + (traceName ?? methodName);
      const span = tracer.startSpan(spanName?.toString() ?? methodName);

      try {
        const result = await originalMethod.apply(this, args);

        // Add custom attributes including target (class) name
        span.setAttributes({
          'class.name': targetName,
          'method.name': methodName,
          'trace.name': spanName
        });

        return result;
      } catch (error: unknown) {
        if (error instanceof Error) {
          span.recordException(error);
        }
        throw error;
      } finally {
        span.end();
      }
    } as Fn;

    return descriptor;
  };
}
